import { Button } from "@/components/ui/button";
import { Menu, Calendar } from "lucide-react";
import { useState, useEffect, memo } from "react";
import { Link, useLocation } from "wouter";
import { useViewingLead } from "@/contexts/ViewingLeadContext";
import logoImage from "@assets/Logo design (7)_1760256523607_opt.png";

// Site-wide announcement banner
const SHOW_RESET_BANNER = true;


// Isolated so the once-a-second tick only re-renders this small text node,
// not the whole Navigation bar (logo, links, mobile menu, buttons).
const GMTClock = memo(function GMTClock() {
  // Starts as null (rather than `new Date()`) so the very first render -
  // prerendered server markup and the client's initial hydration pass -
  // produces identical, empty output. `new Date()` would differ by
  // definition between the build-time prerender and the moment a real
  // visitor's browser hydrates, causing a guaranteed hydration mismatch on
  // every prerendered page. The real time is only ever set client-side,
  // after mount, which React treats as a normal post-hydration update.
  const [currentTime, setCurrentTime] = useState<Date | null>(null);

  useEffect(() => {
    setCurrentTime(new Date());
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (!currentTime) {
    return <div className="hidden md:block text-base text-foreground font-bold" data-testid="gmt-clock" />;
  }

  // "Europe/London" (not the fixed "GMT" zone) so the clock reflects British Summer
  // Time correctly in summer instead of always showing UTC and running an hour behind.
  const formatted = currentTime.toLocaleTimeString('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  const zoneLabel = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    timeZoneName: 'short',
  })
    .formatToParts(currentTime)
    .find((part) => part.type === 'timeZoneName')?.value ?? 'GMT';

  return (
    <div className="hidden md:block text-base text-foreground font-bold" data-testid="gmt-clock">
      {formatted} {zoneLabel}
    </div>
  );
});

export default function Navigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const { openModal } = useViewingLead();

  const closeMenu = () => setMobileMenuOpen(false);

  const isActive = (path: string) => location === path;

  const isBookViewingPage = location === '/book-viewing' || location === '/book-viewing/poole' || location === '/book-viewing/bournemouth';
  const isFirstSessionPage = location === '/first-session' || location === '/first-session/poole' || location === '/first-session/bournemouth';
  const isVisitorBookingPage = location === '/booking' || location === '/booking/';

  // The Poole/Bournemouth booking pages have their own single mid-page offer
  // section (see SiteViewingPage.tsx) so the sitewide top bar would duplicate
  // it - kept off these routes the same way it's off the generic /book-viewing page.
  const hideBannerPages = ['/first-session', '/first-session/poole', '/first-session/bournemouth', '/january', '/private-access-basepoint', '/book-viewing', '/book-viewing/poole', '/book-viewing/bournemouth'];
  const showBanner = SHOW_RESET_BANNER && !isVisitorBookingPage && !hideBannerPages.includes(location);

  // Route-aware: when the visitor is already looking at a site-specific
  // viewing page, send the banner to the matching site-specific first
  // session page instead of the generic chooser.
  const bannerHref =
    location === '/book-viewing/poole' ? '/first-session/poole'
    : location === '/book-viewing/bournemouth' ? '/first-session/bournemouth'
    : '/first-session';

  const isMinimalHeaderPage = location === '/quiet-private-gym' || isFirstSessionPage || isBookViewingPage;

  // Minimal header variant for landing pages
  if (isMinimalHeaderPage) {
    return (
      <>
        {showBanner && (
          <Link href={bannerHref}>
            <div className="fixed top-0 left-0 right-0 z-[60] bg-[#cbb26b] py-2 px-4 cursor-pointer hover:bg-[#b9a35f] transition-colors" data-testid="first-session-banner">
              <p className="text-center text-sm text-[#000000] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
                <span className="sm:hidden">Whole Gym · £14.99 First Session →</span>
                <span className="hidden sm:inline">The Whole Gym To Yourself · £14.99 First Session · Book Now →</span>
              </p>
            </div>
          </Link>
        )}
        <nav className={`fixed ${showBanner ? 'top-[36px]' : 'top-0'} w-full z-50 bg-[#141414]/95 backdrop-blur-md border-b border-border/50`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-between h-14 sm:h-16">
              <Link href="/">
                <div className="flex items-center space-x-2 cursor-pointer" aria-label="Effortless Gym home">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-md shadow-sm overflow-hidden">
                    <img 
                      src={logoImage} 
                      alt="Effortless Gym Logo" 
                      className="w-full h-full object-contain"
                      width={40}
                      height={40}
                    />
                  </div>
                  <span className="font-alata text-sm sm:text-base font-bold">Effortless Gym</span>
                </div>
              </Link>

              <div className="flex items-center space-x-4 sm:space-x-6">
                {!isFirstSessionPage && !isBookViewingPage && (
                  <Link href="/" className="hidden sm:block">
                    <span className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      What is Effortless Gym?
                    </span>
                  </Link>
                )}

                {!isFirstSessionPage && (
                  <Button
                    variant="default"
                    size="sm"
                    className="px-4 sm:px-6 font-medium text-sm"
                    data-testid="nav-minimal-tour-cta"
                    onClick={() => {
                      if (isBookViewingPage) {
                        document.getElementById("book-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
                      } else {
                        openModal("Navigation Minimal Header");
                      }
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-1.5 hidden sm:inline" />
                    {isBookViewingPage ? "Book A Viewing" : "See the Gym First"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </nav>
      </>
    );
  }

  return (
    <>
      {showBanner && (
        <Link href={bannerHref}>
          <div className="fixed top-0 left-0 right-0 z-[60] bg-[#cbb26b] py-2 px-4 cursor-pointer hover:bg-[#b9a35f] transition-colors" data-testid="first-session-banner">
            <p className="text-center text-sm text-[#000000] font-medium whitespace-nowrap overflow-hidden text-ellipsis">
              <span className="sm:hidden">Whole Gym · £14.99 First Session →</span>
              <span className="hidden sm:inline">The Whole Gym To Yourself · £14.99 First Session · Book Now →</span>
            </p>
          </div>
        </Link>
      )}
      <nav className={`fixed ${showBanner ? 'top-[36px]' : 'top-0'} w-full z-50 ${mobileMenuOpen ? 'bg-[#141414]' : 'bg-[#141414]/95 backdrop-blur-md'} border-b border-border/50`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link href="/">
            <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer" onClick={closeMenu} aria-label="Effortless Gym home">
              {/* Effortless Gym Logo */}
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md shadow-sm overflow-hidden">
                <img 
                  src={logoImage} 
                  alt="Effortless Gym Logo" 
                  className="w-full h-full object-contain"
                  width={48}
                  height={48}
                />
              </div>
              <span className="font-alata text-base sm:text-lg font-bold">Effortless Gym</span>
            </div>
          </Link>

          <GMTClock />

          <div className="hidden min-[1400px]:flex items-center space-x-6">
            <Link href="/membership" onClick={closeMenu}>
              <span
                className={`transition-colors font-medium ${isActive('/membership') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="nav-membership"
              >
                  Membership
              </span>
            </Link>
            <Link href="/how-it-works" onClick={closeMenu}>
              <span className={`transition-colors font-medium ${isActive('/how-it-works') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-how-it-works">
                How It Works
              </span>
            </Link>
            <Link href="/services" onClick={closeMenu}>
              <span className={`transition-colors font-medium ${isActive('/services') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-services">
                Services
              </span>
            </Link>
            <Link href="/gym-bournemouth-airport" onClick={closeMenu}>
              <span className={`transition-colors font-medium ${isActive('/gym-bournemouth-airport') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-bournemouth-airport">
                Bournemouth Airport
              </span>
            </Link>
            <Link href="/poole" onClick={closeMenu}>
              <span className={`transition-colors font-medium ${isActive('/poole') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-poole">
                Poole
              </span>
            </Link>
            <Link href="/about" onClick={closeMenu}>
              <span className={`transition-colors font-medium ${isActive('/about') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-about">
                About
              </span>
            </Link>
            
            <Link href="/booking" onClick={closeMenu}>
              <Button 
                variant="outline"
                className="px-6 font-medium border-primary text-primary hover:bg-primary/10"
                data-testid="button-book-now-nav"
              >
                Visitor Booking
              </Button>
            </Link>
            {!isVisitorBookingPage && <Link href="/first-session" onClick={closeMenu}>
              <Button
                variant="default"
                className="px-6 font-medium"
                data-testid="button-first-session-nav"
              >
                First Session £14.99
              </Button>
            </Link>}
          </div>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="sm"
            className="min-[1400px]:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="button-mobile-menu"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            <Menu className="w-6 h-6" />
          </Button>
        </div>
        
        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            id="mobile-navigation"
            data-testid="mobile-navigation"
            className={`min-[1400px]:hidden bg-[#141414] overflow-y-auto py-4 space-y-4 ${showBanner ? 'max-h-[calc(100dvh-100px)] sm:max-h-[calc(100dvh-116px)]' : 'max-h-[calc(100dvh-64px)] sm:max-h-[calc(100dvh-80px)]'}`}
          >
            <Link href="/membership" onClick={closeMenu}>
              <span className={`block py-2 transition-colors font-medium text-base ${isActive('/membership') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-mobile-membership">
                Membership
              </span>
            </Link>
            <Link href="/how-it-works" onClick={closeMenu}>
              <span className={`block py-2 transition-colors font-medium text-base ${isActive('/how-it-works') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-mobile-how-it-works">
                How It Works
              </span>
            </Link>
            <Link href="/gym-bournemouth-airport" onClick={closeMenu}>
              <span className={`block py-2 transition-colors font-medium text-base ${isActive('/gym-bournemouth-airport') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-mobile-bournemouth-airport">
                Bournemouth Airport
              </span>
            </Link>
            <Link href="/poole" onClick={closeMenu}>
              <span className={`block py-2 transition-colors font-medium text-base ${isActive('/poole') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-mobile-poole">
                Poole
              </span>
            </Link>
            <Link href="/services" onClick={closeMenu}>
              <span className={`block py-2 transition-colors font-medium text-base ${isActive('/services') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-mobile-services">
                Services
              </span>
            </Link>
            <Link href="/about" onClick={closeMenu}>
              <span className={`block py-2 transition-colors font-medium text-base ${isActive('/about') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-testid="nav-mobile-about">
                About
              </span>
            </Link>
            
            <Link href="/booking" onClick={closeMenu}>
              <Button 
                variant="outline"
                className="w-full px-6 py-3 font-medium text-base border-primary text-primary hover:bg-primary/10"
                data-testid="button-mobile-book-now"
              >
                Visitor Booking
              </Button>
            </Link>
            {!isVisitorBookingPage && <Link href="/first-session" onClick={closeMenu}>
              <Button
                variant="default"
                className="w-full px-6 py-3 font-medium text-base"
                data-testid="button-mobile-first-session"
              >
                First Session £14.99
              </Button>
            </Link>}
          </div>
        )}
      </div>
    </nav>
    </>
  );
}
